/**
 * English - `assistant`: the merchant's AI assistant (the floating launcher,
 * the chat panel and its history) and the one-click storefront setups it
 * offers (`setup.*`: confirm, progress, summary, undo).
 *
 * Counts use `{n}` rather than `{count}` on purpose: these lines have no
 * plural forms, and `count` would send the lookup looking for them.
 */
const assistant: Record<string, string> = {
  title: 'AI assistant',
  launcher: 'Open the AI assistant',
  launcher_short: 'Ask AI',
  close: 'Close the assistant',
  usage: '{used} of {limit} messages this month',
  usage_unlimited: '{used} messages this month',
  usage_bonus: 'Includes bonus messages while you set up your store.',
  new_conversation: 'New conversation',
  conversation_label: 'Conversation with the AI assistant',
  loading_conversation: 'Loading the conversation…',
  thinking: 'Thinking…',
  stopped: 'Stopped.',
  stop: 'Stop',
  send: 'Send',
  input_label: 'Message the AI assistant',
  input_placeholder: 'Ask anything about setting up your store…',
  input_hint: 'Enter to send, Shift + Enter for a new line.',
  see_plans: 'See plans and credits',

  'empty.greeting': 'Hi {name}! How can I help?',
  'empty.greeting_plain': 'Hi! How can I help?',
  'empty.intro':
    'I can set up your storefront for you, or answer questions about products, payments, shipping and more.',
  'empty.suggestions': 'Try asking',

  'quick_actions.title': 'Quick actions',
  'quick_actions.loading': 'Loading quick actions…',
  // What a quick-action tile shows; the full wording is its tooltip and name.
  'quick_actions.short.FULL_SETUP': 'Set up my entire storefront',
  'quick_actions.short.DEFAULT_SETUP': 'Default settings',
  'quick_actions.short.THEME_AND_HOMEPAGE': 'Theme & homepage',
  'quick_actions.short.PRODUCTS_AND_CATEGORIES': 'Products & categories',
  'quick_actions.short.BANNERS_AND_IMAGES': 'Banners & images',
  'quick_actions.short.PERSONALIZE': 'Personalize with my info',
  'quick_actions.short.OPTIMIZE': 'Optimize storefront',

  'history.title': 'Past conversations',
  'history.back': 'Back to the chat',
  'history.loading': 'Loading conversations…',
  'history.load_failed': 'Your conversations could not be loaded.',
  'history.empty': 'No conversations yet.',
  'history.delete': 'Delete “{title}”',
  'history.delete_confirm': 'Delete “{title}”?',
  'history.delete_cancel': 'Keep',
  'history.delete_yes': 'Delete',
  'history.delete_failed': 'That conversation could not be deleted.',

  'errors.limit':
    'You have used all of this month’s assistant messages. Upgrade your plan for more, or wait until next month.',
  'errors.plan': 'Your plan’s AI allowance is used up, or your plan does not include AI.',
  'errors.ai_off': 'AI is switched off right now. Please try again later.',
  'errors.rate': 'Too many requests. Please wait a moment and try again.',
  'errors.quota': 'You have used all the one-click setups your plan includes this month.',
  'errors.store': 'Create your store first - then one-click setup can fill it in.',
  'errors.unreachable': 'The assistant could not be reached. Check your connection and try again.',

  'entry.one_click': 'Set up my entire storefront in one click',
  'entry.ask': 'Ask the AI assistant',

  'setup.status.pending': 'Waiting',
  'setup.status.running': 'In progress',
  'setup.status.done': 'Done',
  'setup.status.skipped': 'Skipped',
  'setup.status.failed': 'Failed',
  'setup.progress_label': 'Setup progress',
  'setup.changes_title': 'What will change',
  'setup.steps_title': 'Steps',
  'setup.cost_label': 'Cost:',
  'setup.cost.free': 'Free',
  'setup.cost.free_first': 'Free - your first setup',
  'setup.cost.uses': 'Uses {n} AI generations',
  'setup.fix_blocker': 'Fix this',
  'setup.undo_note': 'You can undo everything this setup changes from the summary at the end.',
  'setup.loading_preview': 'Working out what will change…',
  'setup.cancel': 'Cancel',
  'setup.run': 'Run setup',
  'setup.running_title': 'Setting up your storefront…',
  'setup.running_body': 'This usually takes under a minute. You can keep this open to watch each step.',
  'setup.done_title': 'Your storefront is ready!',
  'setup.done_body': 'Here is what was set up for you.',
  'setup.failed_title': 'Setup did not finish',
  'setup.undone_title': 'Changes undone',
  'setup.undone_body': 'Your storefront is back to how it was before this setup.',
  'setup.summary_label': 'What changed',
  'setup.show_steps': 'Show every step',
  'setup.charged': 'Used {n} AI generations.',
  'setup.review': 'Review & Customize',
  'setup.undo': 'Undo',
  'setup.undo_confirm': 'Undo everything this setup changed?',
  'setup.undo_keep': 'Keep changes',
  'setup.undo_yes': 'Yes, undo',
  'setup.undo_failed': 'The changes could not be undone. Please try again.',
  'setup.close': 'Close',
  'setup.store.title': 'Your store',
  'setup.store.intro': 'You don’t have a store yet, so this setup creates it first.',
  'setup.store.name': 'Store name',
  'setup.store.name_invalid': 'Enter a name between 2 and 160 characters.',
  'setup.store.address': 'Web address',
  'setup.store.address_checking': 'Checking this address…',
  'setup.store.address_available': 'This address is available',
  'setup.store.address_invalid': 'Use at least 3 lowercase letters or numbers, with single hyphens between words.',
  'setup.store.address_refused.INVALID': 'This address can’t be used. Try another one.',
  'setup.store.address_refused.RESERVED': 'This address is reserved. Try another one.',
  'setup.store.address_refused.TAKEN': 'This address is already taken.',
  'setup.store.address_try': 'Try:',
  'setup.store.theme': 'Theme',
  'setup.store.change_theme': 'Change theme',
  'setup.store.all_themes': 'All themes',
  'setup.store.search_themes': 'Search by theme name or category',
  'setup.store.no_themes_match': 'No themes match “{query}”.',
  'setup.store.create_and_run': 'Create store & set up',
  'setup.store.step_label': 'Creating your store',
  'setup.store.create_failed': 'Your store could not be created. Please try again.',
  'setup.store.back': 'Back to your store details',
  'setup.store.switch_full': 'Set up my entire storefront',
  'setup.fallback_label.FULL_SETUP': 'Set up my entire storefront',
  'setup.fallback_label.DEFAULT_SETUP': 'Create storefront using default settings',
  'setup.fallback_label.THEME_AND_HOMEPAGE': 'Set up theme and homepage',
  'setup.fallback_label.PRODUCTS_AND_CATEGORIES': 'Add default products & categories',
  'setup.fallback_label.BANNERS_AND_IMAGES': 'Configure banners and images',
  'setup.fallback_label.PERSONALIZE': 'Personalize my storefront with my business information',
  'setup.fallback_label.OPTIMIZE': 'Optimize my entire storefront',
  talk_to_team: 'Talk to the NeonCarts team',
};

export default assistant;
